import React, {
  useState,
  useRef,
  useEffect,
  ChangeEvent,
  DragEvent,
  ReactElement
} from 'react';
import {
  DialogActions,
  DialogContent,
  Dialog,
  makeStyles,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import {useTranslation} from 'react-i18next';
import './ImageUploadWithPreview.scss';

interface ImageUploadWithPreviewProps {
  onFileSelect?: (file: File | null,isReplaced:boolean) => void;
  placeholder?: string;
  typesAllowed?: string[];
  isRequired?: boolean;
  defaultImage?: string | null;
  previouslyExits:boolean
}

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiToolbar-regular': {
      minHeight: 0
    },
    '& .MuiOutlinedInput-root': {
      border: 0
    }
  },
  button: {
    borderRadius: 8,
    width: '5.1vw',
    height: '65%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    textTransform: 'none',
    float: 'right'
  },
  saveButton: {
    borderRadius: 8,
    width: '6.1vw',
    height: '65%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    textTransform: 'none',
    float: 'right',
  },
  dialog: {
    '& .MuiDialog-paperScrollPaper': {
      borderRadius: 8,
      boxShadow: '0 10px 30px 0 rgba(0, 0, 0, 0.05)',
    },
  },
}));

const ImageUploadWithPreview = ({
  onFileSelect,
  placeholder = 'Add front picture of card',
  typesAllowed,
  isRequired,
  defaultImage,
  previouslyExits,
}: ImageUploadWithPreviewProps): JSX.Element => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    defaultImage || null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const classes = useStyles();
  const [isReplaced, setIsReplaced] = useState(false);
  const [dialogueTitle, setDialogueTitle] = useState<string>('')
  const [dialogueText, setDialogueText] = useState<string>('')
  const { t } = useTranslation();
  useEffect(() => {
    if (defaultImage && !previewUrl) {
      setPreviewUrl(defaultImage);
    }
    let revokeTimeout: NodeJS.Timeout;
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:') && previewUrl !== defaultImage) {
        revokeTimeout = setTimeout(() => {
          URL.revokeObjectURL(previewUrl);
        }, 30000); // Delay revocation by 30 seconds
      }
      if (revokeTimeout) clearTimeout(revokeTimeout);
    };
  }, [defaultImage, previewUrl]); // Include previewUrl in dependencies
  const handleFile = (file: File | null) => {
    if (file && file.type.startsWith('image/')) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewUrl(imageUrl);
    } else {
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    onFileSelect && onFileSelect(file,isReplaced);
    setIsReplaced(false);
  };
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (file) {
      // Check if file size is less than or equal to 15MB (15 * 1024 * 1024 bytes)
      if (typesAllowed && !typesAllowed.includes(file.type)) {
        setDialogueTitle(t('PATIENT_DOCUMENT_UPLOAD.DOCUMENT_NOT_ALLOWED'))
        setDialogueText(t('PATIENT_DOCUMENT_UPLOAD.DOCUMENT_NOT_ALLOWED_TEXT'))
        setIsDialogOpen(true);

        // Clear the file input value to reset the state
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      if (file.size <= 15 * 1024 * 1024) {
        handleFile(file);
      } else {
        setDialogueTitle(t('PATIENT_DOCUMENT_UPLOAD.SIZE_EXCEED'));
        setDialogueText(t('PATIENT_DOCUMENT_UPLOAD.SIZE_EXCEED_TEXT'));
        setIsDialogOpen(true);
        // Clear the file input value to reset the state
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
    else{
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragOut = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    if(defaultImage && previewUrl){
      setIsReplaced(false);
    } else if (defaultImage === null && previewUrl){
      setIsReplaced(true);
    }
  };

  const renderFileDialog = (): ReactElement => {
    return (
      <Dialog
        open={isDialogOpen}
        maxWidth={'md'}
        className={classes.dialog}
      >
        <DialogContent>
          <div className='form-container'>
            <div className="title-container">
              {dialogueTitle}
            </div>
            <div className='text-field-container'>
              {dialogueText}
            </div>
          </div>
        </DialogContent>
        <DialogActions className="actions-container">
          <Button
            variant="contained"
            className={[classes.saveButton, 'yes-button'].join(' ')}
            onClick={() => { setIsDialogOpen(false) }}
          >
            <span className="button-text">
              {t('COMMON.OK')}
            </span>
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <div className="fileUploadWrapper">
      <div
        className={`fileUploadContainer ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}>
        <input
          type="file"
          accept={typesAllowed?.join(', ') + ", android/force-camera-workaround"}
          onChange={handleFileChange}
          ref={fileInputRef}
          className={'fileInput'}
        />
        {previewUrl ? (
          <img src={previewUrl} alt="Preview" className={'preview'} />
        ) : (
        <div>
          <div className='placeholderContainer' onClick={handleUploadClick} >
            <p className={'placeholder'}>{placeholder}</p>
            <span className='required'>{isRequired && '*'}</span>
          </div>
          <p className='files-accepted'>
            {t('PATIENT_DOCUMENT_UPLOAD.UPLOAD_INSTRUCTIONS')}
            {typesAllowed && typesAllowed.length > 0 && (
              <span>
                {' (' + typesAllowed.map(type => type.replace('image/', '')).join(', ') + ') '}
              </span>
            )}
          </p>
        </div>
        )}
      </div>
      {previewUrl && (
        <p className="replace-or-delete">
          <span className="replace" onClick={handleUploadClick}>
            {t('PATIENT_DOCUMENT_UPLOAD.REPLACE')}
          </span>
          {(!previouslyExits) && t('PATIENT_DOCUMENT_UPLOAD.OR')}
          {(!previouslyExits)
          && <span className="delete" onClick={() => handleFile(null)}>{t('COMMON.DELETE')}</span>
          }
        </p>
      )}
      {renderFileDialog()}
    </div>
  );
};

export default ImageUploadWithPreview;